import { useAutoAnimate } from '~/common/hooks/useAutoAnimate';
import { useAtomValue } from 'jotai';
import { useEffect } from 'react';
import { isCodeEditorViewAtom } from '~/atoms/atoms';
import CodeViewPanel from '../CodeViewPanel';
import StepList from '../steps/StepList';
import ControlBar from './control-bar/ControlBar';
import { ScrollArea } from '@mantine/core';
import { useResetAtom } from 'jotai/utils';
import { autoHealStepAtom } from '~/atoms/recorder-atoms';
import { TOUR_ID } from '~/components/tours/DraftEditorTour';
import { ScrollToStepButton } from './ScrollToStepButton';
import ReadOnlyControlBar from './control-bar/ReadOnlyControlBar';

export default function EditorPanel({
  testContentId,
  readonly,
  selectedTestContentVersion,
  hideControlBar,
}: {
  testContentId: string;
  readonly?: boolean;
  selectedTestContentVersion?: string;
  hideControlBar?: boolean;
}) {
  const [animationRef] = useAutoAnimate();
  const resetAutoHealStep = useResetAtom(autoHealStepAtom);
  const isCodeEditorView = useAtomValue(isCodeEditorViewAtom);

  useEffect(() => {
    return () => {
      resetAutoHealStep();
    };
  }, []);

  return (
    <div
      id={TOUR_ID.EDITOR_PANEL}
      className="relative h-full overflow-auto"
      ref={animationRef}
    >
      <ScrollArea
        scrollbarSize={8}
        type="always"
        className="relative h-full overflow-auto px-2"
      >
        {isCodeEditorView ?
          <div className="py-4">
            <CodeViewPanel testContentId={testContentId} />
          </div>
        : <div className="relative min-h-full pr-2">
            {hideControlBar ?
              // No control bar is shown in readonly mode because we don't want users to edit the test
              <ReadOnlyControlBar className="sticky top-0 z-[51] bg-gradient-to-b from-[var(--mantine-color-body)] from-85% to-transparent" />
            : <ControlBar
                className="sticky top-0 z-[51] mt-3 mb-4"
                readonly={readonly}
                testContentId={testContentId}
                selectedTestContentVersion={selectedTestContentVersion}
              />
            }
            <StepList
              className="h-full pb-20"
              testContentId={testContentId}
              readonly={readonly}
            />
          </div>
        }
        <ScrollToStepButton />
      </ScrollArea>
    </div>
  );
}
